import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { DownloadButton } from "@/components/DownloadButton";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type DataUploadConfigDetailsProps = {
  configId: number;
  client: string;
  table: string;
  templateFileName: string;
  usageDescription: string;
};

export function DataUploadConfigDetails({
  configId,
  client,
  table,
  templateFileName,
  usageDescription,
}: DataUploadConfigDetailsProps) {
  const { t } = useTranslation(["tables"]);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);

  const hasInstructions = Boolean(usageDescription);
  const instructionsLabel = t("data_upload.labels.user_guide", { ns: "tables" });
  const closeLabel = t("data_upload.actions.close", { ns: "tables" });
  const instructionsAriaLabel = t("data_upload.actions.view_instructions", { ns: "tables" });
  const downloadTemplateLabel = t("data_upload.actions.download_template", { ns: "tables" });

  return (
    <>
      <div className="flex flex-col items-start gap-1">
        <DownloadButton
          href={`${import.meta.env.VITE_BACKEND_URL}/${client}/${table}/configs/${configId}/template`}
          filename={templateFileName}
          size="sm"
          variant="light"
          color="primary"
          text={downloadTemplateLabel}
          className="shrink-0 min-w-0"
        />
        {hasInstructions && (
          <Button
            size="sm"
            variant="light"
            color="primary"
            aria-label={instructionsAriaLabel}
            onPress={() => setIsInstructionsModalOpen(true)}
            className="shrink-0 min-w-0"
          >
            <InformationCircleIcon className="size-4 mr-1" />
            {instructionsLabel}
          </Button>
        )}
      </div>
      <Modal
        backdrop="blur"
        isOpen={isInstructionsModalOpen}
        onClose={() => setIsInstructionsModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>{instructionsLabel}</ModalHeader>
          <ModalBody>
            <div className="whitespace-pre-wrap text-sm text-default-700">
              {usageDescription}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              variant="light"
              onPress={() => setIsInstructionsModalOpen(false)}
            >
              {closeLabel}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
